import { Image, User } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useActiveTenant } from '../../../hooks/common/useActiveTenant'
import { useAuth } from '../../../hooks/useAuth'
import { useFileUpload } from '../../../hooks/useFileUpload'


import { FileUpload } from '@/components/ui/file-upload'
import { showToast } from '@/utils/toastMessage'

interface Props {
  onChange: (event: string) => void
  src: string
  alt: string
  chooseLabel: string
  url: string
  message: string
  placeholderIcon?: 'user' | 'image'
  disabled?: boolean
  disabledMessage?: string
}

const CustomFileUpload = ({
  onChange,
  src,
  chooseLabel,
  alt,
  url,
  message,
  placeholderIcon,
  disabled = false,
  disabledMessage,
}: Props) => {
  const { VITE_APP_API_URL } = import.meta.env
  const { tenant } = useActiveTenant()
  const { getAccessTokenSilently } = useAuth()

  const [token, setToken] = useState<string>('')

  useEffect(() => {
    getAccessTokenSilently().then(setToken)
  }, [getAccessTokenSilently])

  const { uploadImageCompletedHandler, uploadImageErrorHandler } = useFileUpload()

  const handleUpload = async (event: { xhr: XMLHttpRequest; formData: FormData }) => {
    event.xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    event.xhr.setRequestHeader('tenantid', `${tenant?.tenantId}`)
  }

  const handleDisabledClick = () => {
    if (disabled && disabledMessage) {
      showToast({
        severity: 'warn',
        detail: disabledMessage,
      })
    }
  }

  const isDisabled = !token || disabled

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="bg-muted flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg border">
        {src ? (
          <img alt={alt} src={src} className="h-full w-full object-contain p-2" />
        ) : placeholderIcon === 'user' ? (
          <User className="text-muted-foreground h-16 w-16" />
        ) : (
          <Image className="text-muted-foreground h-16 w-16" />
        )}
      </div>
      <div onClick={handleDisabledClick} className="w-full">
        <FileUpload
          mode="basic"
          auto
          chooseLabel={chooseLabel}
          name="image"
          disabled={isDisabled}
          accept="image/*"
          maxFileSize={10000000}
          url={`${VITE_APP_API_URL}/${url}`}
          onBeforeSend={handleUpload}
          onError={uploadImageErrorHandler}
          onUpload={uploadImageCompletedHandler(onChange, message)}
        />
      </div>
    </div>
  )
}

export default CustomFileUpload
